// src/features/auth/AuthModal.js
import { useState } from "react";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import { toast } from "react-toastify";
import { useAuth } from "../../AuthUser";
import API from "../../api";
import BrandBadge from "../../assets/img/brand-badge-large.svg";
import "./auth-modal.css";

function AuthModal({ show, onHide, onSuccess, closable = false }) {
  const auth = useAuth();
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup'
  const [animKey, setAnimKey] = useState(0);
  const [dir, setDir] = useState("in-right"); // "in-left" when going to signup, "in-right" when going to signin

  const swap = (next) => {
    // choose slide direction: signin -> signup slides left; signup -> signin slides right
    setDir(next === "signup" ? "in-left" : "in-right");
    setMode(next);
    setAnimKey((k) => k + 1); // retrigger animation
  };

  const notify = (message) =>
      toast.error(message, { position: toast.POSITION.TOP_RIGHT });

  const initialValues =
      mode === "signin"
          ? { email: "", password: "" }
          : { firstName: "", lastName: "", email: "", password: "", confirmPassword: "" };

  const validate = (values) => {
    const errors = {};
    if (!values.email) errors.email = "Required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email))
      errors.email = "Invalid email address";
    if (!values.password) errors.password = "Required";
    if (mode === "signup") {
      if (!values.firstName) errors.firstName = "Required";
      if (!values.lastName) errors.lastName = "Required";
      if (!values.confirmPassword) errors.confirmPassword = "Passwords must match";
      if (
          values.password &&
          values.confirmPassword &&
          values.password !== values.confirmPassword
      ) {
        errors.password = "Passwords must match";
        errors.confirmPassword = "Passwords must match";
      }
    }
    return errors;
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    const endpoint = mode === "signin" ? "auth/signin" : "auth/signup";
    try {
      // Perform auth first
      await API.post(endpoint, values);
      // Then fetch /accounts/me (your preference) to get the canonical user
      const me = await API.get("/accounts/me");
      if (me?.data) {
        auth.setUser?.(me.data); // immediate, prevents flicker
      }
      if (closable && onHide) onHide();
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error.response?.data?.message) notify(error.response.data.message);
      if (error.response?.data?.errors?.length) {
        const result = error.response.data.errors.reduce((map, obj) => {
          map[obj.property] = obj.message;
          return map;
        }, {});
        setErrors(result);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const niceTitle = mode === "signin" ? "Welcome back" : "Welcome to MyGolfDNA";
  const niceSub =
      mode === "signin"
          ? "To continue, please sign in to your account."
          : "To continue, please create an account.";

  return (
      <Modal
          className="mgd-auth"
          show={show}
          onHide={closable ? onHide : undefined}
          centered
          animation
          backdrop={closable ? true : "static"}
          keyboard={closable ? true : false}
      >
        <Modal.Body>
          {/* Close (X) only when closable */}
          {closable && (
              <button
                  type="button"
                  className="auth-modal-close"
                  aria-label="Close"
                  onClick={onHide}
              >
                ×
              </button>
          )}

          <div className="auth-wrap">
            <div className="title">{niceTitle}</div>
            <div className="subtext">{niceSub}</div>

            <div className={`form-area slide-swap ${dir}`} key={animKey}>
              <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
                {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                      {mode === "signup" && (
                          <Row>
                            <Col sm={12} md={6}>
                              <Form.Group className="mb-3 input-icon">
                          <span className="icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5ZM3 21a9 9 0 0 1 18 0" stroke="currentColor" strokeWidth="1.8" />
                            </svg>
                          </span>
                                <Form.Control
                                    placeholder="First name"
                                    type="text"
                                    name="firstName"
                                    id="firstname"
                                    onChange={handleChange}
                                    value={values.firstName || ""}
                                    isInvalid={errors.firstName && touched.firstName}
                                    isValid={!errors.firstName && touched.firstName}
                                />
                                <div className="invalid-feedback">{errors.firstName}</div>
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={6}>
                              <Form.Group className="mb-3 input-icon">
                          <span className="icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5ZM3 21a9 9 0 0 1 18 0" stroke="currentColor" strokeWidth="1.8" />
                            </svg>
                          </span>
                                <Form.Control
                                    placeholder="Last name"
                                    type="text"
                                    name="lastName"
                                    id="lastname"
                                    onChange={handleChange}
                                    value={values.lastName || ""}
                                    isInvalid={errors.lastName && touched.lastName}
                                    isValid={!errors.lastName && touched.lastName}
                                />
                                <div className="invalid-feedback">{errors.lastName}</div>
                              </Form.Group>
                            </Col>
                          </Row>
                      )}

                      <Form.Group className="mb-3 input-icon">
                    <span className="icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.8" />
                        <rect x="3" y="5" width="18" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="1.8" />
                      </svg>
                    </span>
                        <Form.Control
                            placeholder="Email address"
                            type="email"
                            name="email"
                            id="email"
                            onChange={handleChange}
                            value={values.email}
                            isInvalid={errors.email && touched.email}
                            isValid={!errors.email && touched.email}
                        />
                        <div className="invalid-feedback">{errors.email}</div>
                      </Form.Group>

                      <Form.Group className="mb-3 input-icon">
                    <span className="icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="11" width="18" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="1.8" />
                      </svg>
                    </span>
                        <Form.Control
                            placeholder="Password"
                            type="password"
                            name="password"
                            id="password"
                            onChange={handleChange}
                            value={values.password}
                            isInvalid={errors.password && touched.password}
                        />
                        <div className="invalid-feedback">{errors.password}</div>
                      </Form.Group>

                      {mode === "signup" && (
                          <Form.Group className="mb-3 input-icon">
                      <span className="icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="11" width="18" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="1.8" />
                        </svg>
                      </span>
                            <Form.Control
                                placeholder="Confirm password"
                                type="password"
                                name="confirmPassword"
                                id="confirmpassword"
                                onChange={handleChange}
                                value={values.confirmPassword || ""}
                                isInvalid={errors.confirmPassword && touched.confirmPassword}
                            />
                            <div className="invalid-feedback">{errors.confirmPassword}</div>
                          </Form.Group>
                      )}

                      <div className="d-grid">
                        <Button variant="dark" size="lg" type="submit" className="continue" disabled={isSubmitting}>
                          Continue
                        </Button>
                      </div>

                      <div className="toggle-row text-center">
                        {mode === "signin" ? (
                            <>
                              <Button variant="link" href="/free">
                                Don’t have an account? <strong>Sign up</strong>
                              </Button>
                              <div>
                                <Button variant="link" size="sm" href="/forgot-password">
                                  Forgot your password?
                                </Button>
                              </div>
                            </>
                        ) : (
                            <Button variant="link" onClick={() => swap("signin")}>
                              Already have an account? <strong>Sign in</strong>
                            </Button>
                        )}
                      </div>

                      <div className="footnote">
                        By continuing, you agree to our{" "}
                        <a href="/policies/privacy" target="_blank" rel="noreferrer">
                          Privacy Policy
                        </a>.
                      </div>
                    </Form>
                )}
              </Formik>
            </div>
          </div>
        </Modal.Body>
      </Modal>
  );
}

export default AuthModal;
